import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { paymentsAPI } from '../lib/api';
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PaymentSuccess() {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return; }
    const poll = async () => {
      try {
        const { data } = await paymentsAPI.getStatus(sessionId);
        if (data.payment_status === 'paid') { setStatus('success'); }
        else if (data.status === 'expired') { setStatus('failed'); }
        else if (attempts < 5) { setTimeout(() => setAttempts(a => a + 1), 2000); }
        else { setStatus('failed'); }
      } catch {
        if (attempts < 5) { setTimeout(() => setAttempts(a => a + 1), 2000); } else { setStatus('failed'); }
      }
    };
    poll();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4" data-testid="payment-page">
      <div className="w-full max-w-sm text-center">
        {status === 'checking' && (
          <div data-testid="payment-checking">
            <Loader2 className="w-16 h-16 animate-spin text-[#FADB5F] mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-[#F1F5F9] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('payment.title')}</h1>
            <p className="text-sm text-[#94A3B8]">{t('payment.checking')}</p>
          </div>
        )}
        {status === 'success' && (
          <div data-testid="payment-success">
            <div className="w-16 h-16 rounded-full bg-green-900/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-xl font-semibold text-[#F1F5F9] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('payment.success')}</h1>
            <p className="text-sm text-[#94A3B8] mb-6">{t('payment.successMsg')}</p>
            <Link to="/dashboard">
              <Button className="bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A]" data-testid="back-to-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> {t('payment.backToListings')}
              </Button>
            </Link>
          </div>
        )}
        {status === 'failed' && (
          <div data-testid="payment-failed">
            <div className="w-16 h-16 rounded-full bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-xl font-semibold text-[#F1F5F9] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('payment.failed')}</h1>
            <p className="text-sm text-[#94A3B8] mb-6">{t('payment.failedMsg')}</p>
            <Link to="/listings">
              <Button variant="outline" className="border-[#1E293B] text-[#94A3B8]" data-testid="back-to-listings-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> {t('payment.backToListings')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
